import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useForm, useFieldArray } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  Cog6ToothIcon,
  ClipboardDocumentListIcon,
  IdentificationIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { getSignUpPageSettings, updateSignUpPageSettings } from '../../services/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import PermissionGuard from '../Common/PermissionGuard';

const SIGNUP_SETTINGS_READ_PERMISSION = 'ADMIN:SIGNUP_SETTINGS:READ';
const SIGNUP_SETTINGS_UPDATE_PERMISSION = 'ADMIN:SIGNUP_SETTINGS:UPDATE';

// ─── Default T&C HTML ─────────────────────────────────────────────────────────

const DEFAULT_TERMS_HTML = `<p>These general terms and conditions govern your use of this website and any services made available through it. By creating an account or accessing the site, you agree to comply with these terms.</p><h3>Use of the website</h3><p>You agree to use this website only for lawful purposes and in a way that does not infringe the rights of, restrict or inhibit anyone else's use and enjoyment of the website.</p><h3>Account responsibility</h3><p>You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account. You must notify the site owner immediately of any unauthorised use of your account.</p><h3>Content and availability</h3><p>The website and its content are provided on an "as is" and "as available" basis. The site owner may change, suspend, or discontinue any part of the website at any time without notice.</p><h3>Limitation of liability</h3><p>To the maximum extent permitted by law, the site owner will not be liable for any indirect, incidental, or consequential damages arising out of or in connection with your use of the website.</p><h3>Changes to these terms</h3><p>The site owner may update these terms from time to time. Continued use of the website after changes are posted constitutes your acceptance of the revised terms.</p>`;

// ─── Shared input class helper ────────────────────────────────────────────────

const inputCls = (hasErr) =>
  `block w-full px-3 py-2.5 border rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
    hasErr ? 'border-red-300' : 'border-gray-300 hover:border-gray-400'
  }`;

// ─── Rich text editor ─────────────────────────────────────────────────────────

const ToolbarBtn = ({ onClick, active, disabled, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    disabled={disabled}
    title={title}
    className={`px-2 py-1 rounded text-xs font-medium transition-colors leading-none select-none ${
      active ? 'bg-indigo-100 text-indigo-700' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
    } disabled:opacity-40 disabled:cursor-not-allowed`}
  >
    {children}
  </button>
);

const Divider = () => <span className="w-px h-5 bg-gray-300 mx-1 self-center flex-shrink-0" />;

const RichEditor = React.forwardRef(({ initialContent, onChange, disabled }, ref) => {
  const editor = useEditor({
    extensions: [StarterKit],
    content: initialContent || '',
    editable: !disabled,
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: { attributes: { class: 'outline-none min-h-[340px] px-4 py-3 text-sm text-gray-900' } },
  });

  React.useImperativeHandle(ref, () => ({
    setContent: (html) => editor?.commands.setContent(html, false),
    getHTML: () => editor?.getHTML() ?? '',
  }));

  if (!editor) return null;

  return (
    <div className={`border rounded-lg overflow-hidden transition-colors ${disabled ? 'border-gray-200 bg-gray-50' : 'border-gray-300 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500'}`}>
      {!disabled && (
        <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50">
          <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold"><strong>B</strong></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic"><em>I</em></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough"><s>S</s></ToolbarBtn>
          <Divider />
          {[1, 2, 3].map((level) => (
            <ToolbarBtn key={level} onClick={() => editor.chain().focus().toggleHeading({ level }).run()} active={editor.isActive('heading', { level })} title={`Heading ${level}`}>H{level}</ToolbarBtn>
          ))}
          <Divider />
          <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">• List</ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Ordered list">1. List</ToolbarBtn>
          <Divider />
          <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">❝</ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} active={false} title="Horizontal rule">——</ToolbarBtn>
          <span className="flex-1" />
          <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo"><ArrowUturnLeftIcon className="w-3.5 h-3.5" /></ToolbarBtn>
          <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo"><ArrowUturnRightIcon className="w-3.5 h-3.5" /></ToolbarBtn>
        </div>
      )}
      <div className="[&_.tiptap_p]:mb-2 [&_.tiptap_p:last-child]:mb-0 [&_.tiptap_h1]:text-xl [&_.tiptap_h1]:font-bold [&_.tiptap_h1]:mt-4 [&_.tiptap_h1]:mb-1 [&_.tiptap_h2]:text-base [&_.tiptap_h2]:font-semibold [&_.tiptap_h2]:mt-3 [&_.tiptap_h2]:mb-1 [&_.tiptap_h3]:text-sm [&_.tiptap_h3]:font-semibold [&_.tiptap_h3]:mt-3 [&_.tiptap_h3]:mb-1 [&_.tiptap_ul]:list-disc [&_.tiptap_ul]:pl-5 [&_.tiptap_ul]:mb-2 [&_.tiptap_ol]:list-decimal [&_.tiptap_ol]:pl-5 [&_.tiptap_ol]:mb-2 [&_.tiptap_li]:mb-0.5 [&_.tiptap_blockquote]:border-l-4 [&_.tiptap_blockquote]:border-gray-300 [&_.tiptap_blockquote]:pl-4 [&_.tiptap_blockquote]:text-gray-600 [&_.tiptap_blockquote]:italic [&_.tiptap_blockquote]:my-2 [&_.tiptap_hr]:border-gray-200 [&_.tiptap_hr]:my-3">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
});

RichEditor.displayName = 'RichEditor';

// ─── Terms & Conditions panel ─────────────────────────────────────────────────

const TermsPanel = ({ canRead, canUpdate }) => {
  const editorRef    = useRef(null);
  const savedHTMLRef = useRef('');
  const [initialContent, setInitialContent] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canRead) { setLoading(false); return; }
    (async () => {
      try {
        const res   = await getSignUpPageSettings();
        const saved = res.data?.data?.termsAndConditions || '';
        savedHTMLRef.current = saved;
        setInitialContent(saved || DEFAULT_TERMS_HTML);
      } catch {
        toast.error('Failed to load settings');
        setInitialContent(DEFAULT_TERMS_HTML);
      } finally {
        setLoading(false);
      }
    })();
  }, [canRead]);

  const handleChange = useCallback((html) => {
    setIsDirty(html !== savedHTMLRef.current);
  }, []);

  const handleSave = async () => {
    const html = editorRef.current?.getHTML() ?? '';
    setSaving(true);
    try {
      await updateSignUpPageSettings({ termsAndConditions: html });
      savedHTMLRef.current = html;
      setIsDirty(false);
      toast.success('Terms and conditions saved');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-center gap-3"><div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /><span className="text-sm text-gray-500">Loading…</span></div>;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">Terms and Conditions</h3>
        <p className="mt-0.5 text-xs text-gray-500">Displayed in the sign-up modal when a user clicks &quot;terms and conditions&quot;.</p>
      </div>
      <div className="p-4 space-y-3">
        {initialContent !== null && (
          <RichEditor ref={editorRef} initialContent={initialContent} onChange={handleChange} disabled={!canUpdate} />
        )}
        {canUpdate && (
          <div className="flex items-center justify-between pt-1">
            <button type="button" onClick={() => { editorRef.current?.setContent(DEFAULT_TERMS_HTML); setIsDirty(DEFAULT_TERMS_HTML !== savedHTMLRef.current); }} className="text-xs text-gray-500 hover:text-indigo-600 font-medium transition-colors">
              Restore built-in default
            </button>
            <button type="button" onClick={handleSave} disabled={saving || !isDirty} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {saving && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── General Contact Details panel ───────────────────────────────────────────

const ContactPanel = ({ canRead, canUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  const { register, handleSubmit, control, reset, formState: { errors, isDirty } } = useForm({
    defaultValues: {
      generalContactPersonName: '',
      generalContactEmail: '',
      generalContactPhones: [{ value: '' }],
      generalContactAddress: '',
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'generalContactPhones' });

  useEffect(() => {
    if (!canRead) { setLoading(false); return; }
    (async () => {
      try {
        const res  = await getSignUpPageSettings();
        const data = res.data?.data || {};
        reset({
          generalContactPersonName: data.generalContactPersonName || '',
          generalContactEmail: data.generalContactEmail || '',
          generalContactPhones:
            data.generalContactPhones?.length > 0
              ? data.generalContactPhones.map((p) => ({ value: p }))
              : [{ value: '' }],
          generalContactAddress: data.generalContactAddress || '',
        });
      } catch {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, [canRead, reset]);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      await updateSignUpPageSettings({
        generalContactPersonName: data.generalContactPersonName.trim(),
        generalContactEmail: data.generalContactEmail.trim(),
        generalContactPhones: data.generalContactPhones.map((p) => p.value.trim()).filter(Boolean),
        generalContactAddress: data.generalContactAddress.trim(),
      });
      toast.success('Contact details saved');
      reset(data);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="bg-white rounded-xl border border-gray-200 p-6 flex items-center gap-3"><div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /><span className="text-sm text-gray-500">Loading…</span></div>;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">General Contact Details</h3>
        <p className="mt-0.5 text-xs text-gray-500">
          Shown on the sign-up page as &quot;For enquiries, contact us:&quot; for all users. Leave all fields blank to hide.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Contact person name</label>
            <input {...register('generalContactPersonName', { maxLength: { value: 120, message: 'Too long (max 120)' } })} type="text" placeholder="e.g. John Smith" className={inputCls(!!errors.generalContactPersonName)} disabled={!canUpdate} />
            {errors.generalContactPersonName && <p className="mt-0.5 text-xs text-red-600">{errors.generalContactPersonName.message}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Contact email</label>
            <input {...register('generalContactEmail', { validate: (v) => !v || /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(v.trim()) || 'Invalid email address' })} type="email" placeholder="e.g. info@company.com" className={inputCls(!!errors.generalContactEmail)} disabled={!canUpdate} />
            {errors.generalContactEmail && <p className="mt-0.5 text-xs text-red-600">{errors.generalContactEmail.message}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-600">Phone number(s)</label>
              {canUpdate && fields.length < 10 && (
                <button type="button" onClick={() => append({ value: '' })} className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700">
                  <PlusIcon className="w-3.5 h-3.5" />Add
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {fields.map((field, index) => (
                <div key={field.id} className="flex items-center gap-1">
                  <input {...register(`generalContactPhones.${index}.value`, { maxLength: { value: 30, message: 'Too long' } })} type="tel" placeholder="07XXXXXXXX" className={`flex-1 min-w-0 ${inputCls(!!(errors.generalContactPhones?.[index]?.value))}`} disabled={!canUpdate} />
                  {canUpdate && fields.length > 1 && (
                    <button type="button" onClick={() => remove(index)} className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 rounded hover:bg-red-50 transition-colors" title="Remove">
                      <TrashIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
            <textarea {...register('generalContactAddress', { maxLength: { value: 500, message: 'Too long (max 500)' } })} rows={Math.max(fields.length, 2)} placeholder="e.g. 123 Main Street, Colombo 03" className={`resize-none ${inputCls(!!errors.generalContactAddress)}`} disabled={!canUpdate} />
            {errors.generalContactAddress && <p className="mt-0.5 text-xs text-red-600">{errors.generalContactAddress.message}</p>}
          </div>
        </div>

        {canUpdate && (
          <div className="flex justify-end pt-1">
            <button type="submit" disabled={saving || !isDirty} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {saving && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

// ─── Nav items ────────────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { key: 'contact', label: 'Contact Details', description: 'General enquiry contact shown on the sign-up page', icon: IdentificationIcon },
  { key: 'terms',   label: 'Terms & Conditions', description: 'Custom T&C shown in the registration modal', icon: ClipboardDocumentListIcon },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

const SignUpSettings = () => {
  const { hasPermission } = useAuth();
  const canUpdate = hasPermission(SIGNUP_SETTINGS_UPDATE_PERMISSION);
  const canRead   = hasPermission(SIGNUP_SETTINGS_READ_PERMISSION) || canUpdate;

  const [activeKey, setActiveKey] = useState('contact');

  return (
    <PermissionGuard
      permission={[SIGNUP_SETTINGS_READ_PERMISSION, SIGNUP_SETTINGS_UPDATE_PERMISSION]}
      fallback={
        <div className="bg-white shadow rounded-lg p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Sign-Up Page Settings</h1>
          <p className="text-gray-600">You don&apos;t have permission to view Sign-Up Page Settings.</p>
        </div>
      }
      showFallback
    >
      {/* Page header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center">
          <Cog6ToothIcon className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Sign-Up Page Settings</h1>
          <p className="text-xs text-gray-500">Configure content displayed on the public registration page.</p>
        </div>
      </div>

      <div className="flex gap-5 items-start">
        {/* Sidebar */}
        <nav className="flex-shrink-0 w-52 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <p className="px-3 pt-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Configuration</p>
          <ul className="pb-2">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = item.key === activeKey;
              return (
                <li key={item.key}>
                  <button type="button" onClick={() => setActiveKey(item.key)} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${isActive ? 'bg-indigo-50 text-indigo-700 font-medium' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}>
                    <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                    <span className="truncate">{item.label}</span>
                    {isActive && <span className="ml-auto w-1 h-4 rounded-full bg-indigo-500" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Content panel */}
        <div className="flex-1 min-w-0">
          {activeKey === 'contact' && <ContactPanel canRead={canRead} canUpdate={canUpdate} />}
          {activeKey === 'terms'   && <TermsPanel   canRead={canRead} canUpdate={canUpdate} />}
        </div>
      </div>
    </PermissionGuard>
  );
};

export default SignUpSettings;
